/*
 * WHERE the delivery chart's hover reading is allowed to sit.
 *
 * The reading used to be `position: absolute; top: 12px; right: 12px` inside
 * `.cpx-chart-wrap`, which made its visibility a property of whatever box the
 * chart happened to be dropped into. On the Cortex and Quantum welcome
 * dashboards that box is `.cortex-welcome-panel`, which carries
 * `overflow: hidden` for its 28px rounded frame and its ::before/::after
 * corner washes — so a tooltip taller than the remaining card simply had its
 * lower rows cut off at the panel edge. Nothing about the tooltip was wrong;
 * it was being asked to stay inside a frame that is smaller than it is.
 *
 * So the reading is placed against the VIEWPORT instead, from the hovered
 * column's real rectangle, and the renderer portals it out of the panel. This
 * module is the geometry half of that: pure, synchronous, and unaware of React
 * or the DOM, so the placement contract can be pinned directly rather than
 * inferred from a screenshot.
 *
 * The rules it encodes:
 *   - the reading never leaves the viewport, and never touches its edge: a
 *     gutter is reserved on all four sides;
 *   - it sits beside the column it describes, preferring the side with room —
 *     right, then left, then below, then above;
 *   - when no side fits it takes the roomiest one rather than the first;
 *   - when it is taller than the viewport allows it is CONSTRAINED, never
 *     cropped: the caller gets `maxHeight` plus `constrained: true` and makes
 *     the content scrollable, so no platform, campaign, total or outcome row
 *     is ever silently dropped.
 */

/* The safety gutter between the reading and the edge of the viewport. Mirrored
   by the floating tooltip's `max-width` in DeliveryChart.css, which reserves
   the same gutter on both sides so a narrow viewport can never be given
   horizontal overflow by this component. */
export const CHART_TOOLTIP_GUTTER = 12;

/* The gap between the hovered column and the reading. Small enough that the
   reading still visually belongs to the column, large enough that it does not
   sit on the bar it is describing. */
export const CHART_TOOLTIP_ANCHOR_GAP = 10;

const SIDES = ["right", "left", "bottom", "top"];

function clamp(value, min, max) {
  /* A viewport smaller than the reading would invert the bounds; pinning to
     `min` keeps the top-left corner visible rather than pushing it off-screen
     in the name of fitting the bottom-right one. */
  if (min > max) return min;
  return Math.min(Math.max(value, min), max);
}

/*
 * The rectangle the reading has to stay inside.
 *
 * `visualViewport` is what actually moves under pinch-zoom and under the
 * on-screen keyboard, so it is preferred where it exists; `offsetLeft`/
 * `offsetTop` place it back in client coordinates, which is the space
 * `getBoundingClientRect()` reports in. Browser (page) zoom needs no special
 * handling — it scales CSS pixels, and both rectangles are already read in
 * them.
 */
export function resolveTooltipViewport(view) {
  const visual = view && view.visualViewport;
  if (visual && visual.width > 0 && visual.height > 0) {
    return {
      left: visual.offsetLeft || 0,
      top: visual.offsetTop || 0,
      width: visual.width,
      height: visual.height,
    };
  }
  return {
    left: 0,
    top: 0,
    width: (view && view.innerWidth) || 0,
    height: (view && view.innerHeight) || 0,
  };
}

export function placeChartTooltip({
  anchor,
  tooltip,
  viewport,
  gutter = CHART_TOOLTIP_GUTTER,
  anchorGap = CHART_TOOLTIP_ANCHOR_GAP,
}) {
  const innerLeft = viewport.left + gutter;
  const innerTop = viewport.top + gutter;
  const innerRight = viewport.left + viewport.width - gutter;
  const innerBottom = viewport.top + viewport.height - gutter;

  const maxWidth = Math.max(0, innerRight - innerLeft);
  const maxHeight = Math.max(0, innerBottom - innerTop);

  /* What the reading will actually occupy once the viewport has had its say —
     the natural size is what the caller measured, the used size is what fits. */
  const width = Math.min(tooltip.width, maxWidth);
  const height = Math.min(tooltip.height, maxHeight);
  /* Sub-pixel measurement noise must not latch a scrollbar onto a reading that
     fits, so the comparison carries a half-pixel of slack. */
  const constrained = tooltip.height > maxHeight + 0.5;

  const anchorRight = anchor.left + anchor.width;
  const anchorBottom = anchor.top + anchor.height;

  const room = {
    right: innerRight - (anchorRight + anchorGap),
    left: anchor.left - anchorGap - innerLeft,
    bottom: innerBottom - (anchorBottom + anchorGap),
    top: anchor.top - anchorGap - innerTop,
  };
  const needed = (side) => (side === "right" || side === "left" ? width : height);

  /* First side that genuinely fits, in preference order. Beside the column
     beats above or below it: a stacked-bar reading lines up with the column it
     belongs to far more legibly from the side. */
  let placement = SIDES.find((side) => room[side] >= needed(side));
  if (!placement) {
    /* Nothing fits — take the side with the largest shortfall margin rather
       than the first one tried, then let the clamp below keep it on screen. */
    placement = SIDES.reduce(
      (best, side) => (room[side] - needed(side) > room[best] - needed(best) ? side : best),
      SIDES[0]
    );
  }

  let left;
  let top;
  if (placement === "right" || placement === "left") {
    left = placement === "right" ? anchorRight + anchorGap : anchor.left - anchorGap - width;
    /* Centred on the column so the reading tracks the hovered point vertically
       rather than jumping between two fixed rails. */
    top = anchor.top + anchor.height / 2 - height / 2;
  } else {
    top = placement === "bottom" ? anchorBottom + anchorGap : anchor.top - anchorGap - height;
    left = anchor.left + anchor.width / 2 - width / 2;
  }

  return {
    left: clamp(left, innerLeft, innerRight - width),
    top: clamp(top, innerTop, innerBottom - height),
    placement,
    /* Both caps are published: the stylesheet applies the same width bound via
       the gutter custom property, and a caller that needs to know the box it
       will actually paint should not have to re-derive either of them. */
    maxWidth,
    maxHeight,
    constrained,
  };
}
